import PropTypes from "prop-types";
import React, { useMemo, useCallback } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { MultiValue } from "./MultiValue";

export const FormDropDown = ({
  options,
  labelName,
  id,
  required = false,
  requiredAny = false,
  onChange,
  value,
  isMulti = false,
  error,
  selectAll = false,
  creatable = false,
}) => {
  
  const borderColor = error ? "border-red-500" : "border-gray-300";
  const borderFocus = error ? "border-red-500" : "border-teal-300";

  const makeOptions = (x) => ({ value: x, label: x });

  const sortedOptions = useMemo(() => {
    if (creatable) {
      const validOptions = Array.isArray(options) ? options : [];
      return [...validOptions].sort((a, b) => a.localeCompare(b));
    }
    return options?.map((x) => makeOptions(x)) || [];
  }, [options, creatable]);

  const allOption = useMemo(() => ({ label: "Select All", value: "*" }), []);

  const optionsWithSelectAll = useMemo(() => {
    if (selectAll && isMulti) {
      if (creatable) {
        return [allOption, ...sortedOptions.map(makeOptions)];
      }
      return [allOption, ...sortedOptions];
    }
    if (creatable) {
      return sortedOptions.map(makeOptions);
    }
    return sortedOptions;
  }, [selectAll, isMulti, sortedOptions, allOption, creatable]);

  const isAllSelected = useCallback(() => {
    if (!Array.isArray(value)) return false;
    const optionValues = creatable ? sortedOptions : sortedOptions.map((opt) => opt.value);
    const selectedValues = value.map((v) => v.value);
    return optionValues.every((opt) => selectedValues.includes(opt));
  }, [value, sortedOptions, creatable]);

  const handleChange = (selected) => {
    if (!isMulti) {
      onChange(selected);
      return;
    }
    if (!selected) {
      onChange([]);
      return;
    }
    const isSelectAllSelected = selected.some((opt) => opt.value === allOption.value);
    if (isSelectAllSelected) {
      if (isAllSelected()) {
        onChange([]);
      } else {
        onChange(creatable ? sortedOptions.map(makeOptions) : sortedOptions);
      }
    } else {
      const filtered = selected.filter((opt) => opt.value !== allOption.value);
      onChange(filtered);
    }
  };

  const adjustedValue = useMemo(() => {
    if (!isMulti || !selectAll) return value;
    if (isAllSelected()) {
      return creatable ? sortedOptions.map(makeOptions) : sortedOptions;
    }
    return value;
  }, [value, isMulti, selectAll, sortedOptions, isAllSelected, creatable]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#00bfae" : "#ccc",
      boxShadow: state.isFocused ? "0 0 0 1px #00bfae" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#00bfae" : "#aaa",
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderColor: "#ccc",
    }),
    option: (provided, state) => {
      let backgroundColor;
      if (state.isSelected) {
        backgroundColor = "#e6f7f7";
      } else if (state.isFocused) {
        backgroundColor = "#f0f0f0";
      } else {
        backgroundColor = "white";
      }
      const color = state.isSelected ? "#00bfae" : "#333";

      return {
        ...provided,
        backgroundColor,
        color,
      };
    },
  };

  return (
    <div className="my-2 mt-0">
      <label className="block font-normal text-lg" htmlFor={id}>
        {labelName}
        {required && <sup className="text-red-500 font-bold text-lg ml-1">*</sup>}
        {requiredAny && <sup className="text-red-500 font-bold text-lg ml-1">**</sup>}
      </label>

      {creatable ? (
        <CreatableSelect
          options={optionsWithSelectAll}
          isMulti={isMulti}
          id={id}
          className={`form-control mt-1 block w-full mt-1 border ${borderColor} rounded-sm shadow-sm`}
          onChange={handleChange}
          value={adjustedValue}
          aria-label={labelName}
          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
          components={{ MultiValue }}
        />
      ) : (
        <Select
          options={optionsWithSelectAll}
          isMulti={isMulti}
          id={id}
          aria-labelledby={id}
          className={`form-control mt-1 block w-full mt-1 border ${borderColor} rounded-sm shadow-sm focus:${borderFocus} focus:ring-0 focus:outline-none`}
          styles={customStyles}
          onChange={handleChange}
          value={adjustedValue}
          aria-label={labelName}
          components={{ MultiValue }}
        />
      )}

      {error && <span className="text-red-500 text-sm">{error.message}</span>}
    </div>
  );
};

FormDropDown.propTypes = {
  labelName: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  ]),
  required: PropTypes.bool,
  requiredAny: PropTypes.bool,
  isMulti: PropTypes.bool,
  error: PropTypes.object,
  id: PropTypes.string,
  selectAll: PropTypes.bool,
  creatable: PropTypes.bool,
};
